/**
 * Topbar — transcrição do protótipo.
 *
 * A busca do cabeçalho é a **global** (`data-global-search="1"`): ela navega para a
 * tela 2.2. O atributo é o contrato que o shell do protótipo usa para distinguir da
 * busca local de uma lista — busca local NUNCA recebe o atributo, e a distinção
 * importa porque o atalho de teclado só pode sequestrar uma delas.
 *
 * O acionamento abre a paleta e leva à página base, que fica acessível ao fechar
 * o painel. Ctrl/⌘ K continua disponível em qualquer tela para a mesma busca.
 */
import { useNavigate } from '@tanstack/react-router'
import { Icone } from './icone.tsx'
import { Sino } from './sino.tsx'
import { SeletorDeEntidade, type Entidade } from './seletor-entidade.tsx'
import { useBuscaGlobal } from '../componentes/busca-global.tsx'
import { SLOT_DA_AJUDA_DA_PAGINA } from '../componentes/sobreposicao.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DO_SHELL } from './shell-textos.ts'

interface Props {
  readonly entidades: readonly Entidade[]
  readonly entidadeAtiva: Entidade | null
  readonly aoTrocarEntidade: (entidade: Entidade) => void
  readonly naoLidas: number
  readonly aoAbrirMenuLateral: () => void
  readonly aoAlternarRail: () => void
  /** A barra está RECOLHIDA (fixa). O botão diz o que ele faz agora: recolher ou fixar aberta. */
  readonly rail: boolean
}

export function Topbar({
  entidades,
  entidadeAtiva,
  aoTrocarEntidade,
  naoLidas,
  aoAbrirMenuLateral,
  aoAlternarRail,
  rail,
}: Props) {
  const texto = useTextos(TEXTOS_DO_SHELL)
  const busca = useBuscaGlobal()
  const navegar = useNavigate()
  const abrirPaginaDaBusca = (): void => {
    void navegar({ to: '/busca' })
    busca.abrir()
  }

  return (
    <header className="sticky top-0 z-20 h-16 bg-paper/85 backdrop-blur border-b border-hairline flex items-center gap-3 px-4 md:px-6">
      <button
        type="button"
        className="md:hidden text-navy"
        onClick={aoAbrirMenuLateral}
        aria-label={texto('abrirMenu')}
      >
        <Icone nome="menu" className="w-5 h-5" />
      </button>

      {/*
        Um botão, dois estados (D-143): aberta fixa, ele recolhe ("Retrair menu"); recolhida,
        ele fixa aberta ("Fixar menu aberto"). O rótulo, o `aria-expanded` e o giro do ícone
        acompanham, como o `#collapseIcon` do protótipo. No Tailwind 4, `rotate-180` é a
        propriedade `rotate`, e não `transform`.
      */}
      <button
        type="button"
        className="hidden md:grid place-items-center h-9 w-9 rounded-[10px] text-text2 hover:bg-navy/5 shrink-0"
        onClick={aoAlternarRail}
        aria-label={texto(rail ? 'fixarMenu' : 'retrairMenu')}
        title={texto(rail ? 'fixarMenu' : 'retrairMenu')}
        aria-expanded={!rail}
        aria-controls="sidebar"
      >
        <Icone nome="panel-left" className={'w-[18px] h-[18px]' + (rail ? ' rotate-180' : '')} />
      </button>

      <div className="relative flex-1 min-w-0 max-w-md">
        <Icone nome="search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
        <input
          type="search"
          data-global-search="1"
          placeholder={texto('exemploBusca')}
          aria-label={texto('busca')}
          aria-haspopup="dialog"
          readOnly
          className="w-full h-9 pl-9 pr-3 rounded-[10px] bg-white border border-hairline text-sm placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-navy/70 focus:border-navy"
          onClick={abrirPaginaDaBusca}
          /*
            Abre com Enter/Espaço, e NÃO no foco (lote U1).

            Abrir no foco tinha dois defeitos que só aparecem no teclado: quem tabula
            pela barra superior a caminho de outra coisa recebia um diálogo modal que
            não pediu; e, agora que fechar um diálogo devolve o foco a quem o abriu,
            `Esc` devolveria o foco a este campo — que reabriria a busca. O diálogo
            ficaria impossível de fechar sem o mouse.
          */
          onKeyDown={(evento) => {
            if (evento.key === 'Enter' || evento.key === ' ') {
              evento.preventDefault()
              abrirPaginaDaBusca()
            }
          }}
        />
      </div>

      <div className="flex-1" />

      <SeletorDeEntidade entidades={entidades} ativa={entidadeAtiva} aoTrocar={aoTrocarEntidade} />
      {/*
        O vao da ajuda da pagina, entre a entidade e o sino: a posicao que os desenhos da
        2.1, 2.2, 2.3 e 2.4 usam -- as tres primeiras com a boia "Central de ajuda", a 2.4
        com o "Como ler esta pagina". `display: contents` para que ele nao ocupe nada
        quando a pagina aberta nao tem ajuda de barra -- um item flex vazio custaria o
        `gap` dos dois lados em TODAS as telas, e mexeria no retrato visual de cada uma.
      */}
      <div id={SLOT_DA_AJUDA_DA_PAGINA} className="contents" />
      <Sino naoLidas={naoLidas} />
    </header>
  )
}
